import { useAuth, useClerk } from "@clerk/clerk-react";
import { Button } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";

import { MissingOrganization } from "@/components/auth/missing-organization";
import AuthLoadingScreen from "@/components/loading/auth";
import { CONSTANTS } from "@/config/constants";
import { useGetAllChannels } from "@/features/channel/api";
import { useGetOrganization } from "@/hooks/api/organization/useGetOrganization";
import { useGetAllUsers } from "@/hooks/api/user/useGetAllUsers";
import { useGetMe } from "@/hooks/api/user/useGetMe";
import { useGetAllWorkspaces } from "@/hooks/api/workspace/useGetAllWorkspaces";
import { useGetWorkspace } from "@/hooks/api/workspace/useGetWorkspace";
import { useAnalyticsIdentity } from "@/hooks/use-analytics-identity";
import { useLogo } from "@/hooks/use-logo";
import { resetAnalytics } from "@/lib/analytics";
import { queryClient } from "@/main";
import { isMockEnabled } from "@/mock";
import { useMockStore } from "@/mock/store";
import { useGlobalStore } from "@/stores/global";
import { isNotFoundError, isUnauthorizedError } from "@/utils/error";
import {
  clearPreferredWorkspaceId,
  getPreferredWorkspaceId,
  getWorkspaceToActivate,
} from "@/utils/workspace";

interface BootstrapProviderProps {
  children: React.ReactNode;
}

/** Where a 401 is in its one fresh-token retry before it may sign out. */
type UnauthorizedRecovery =
  | "idle"
  | "refreshing"
  | "refreshed"
  | "unavailable"
  | "signingOut";

export const BootstrapProvider = ({ children }: BootstrapProviderProps) => {
  const { userId, isSignedIn, getToken } = useAuth();
  const { signOut } = useClerk();
  const [isLoggedInUserResolved, setIsLoggedInUserResolved] = useState(false);
  const [unauthorizedRecovery, setUnauthorizedRecovery] =
    useState<UnauthorizedRecovery>("idle");

  const {
    activeWorkspaceId,
    activeWorkspace,
    setActiveWorkspaceId,
    setActiveWorkspace,
    setOrganization,
    updateLoggedInUser,
    reset,
  } = useGlobalStore();

  const { full: logoFull } = useLogo();

  useAnalyticsIdentity();

  // Fetch bootstrap data
  const {
    data: organization,
    isLoading: isOrgLoading,
    error: orgError,
    refetch: refetchOrg,
  } = useGetOrganization();

  const {
    data: workspaces,
    isLoading: isWorkspacesLoading,
    error: workspacesError,
    refetch: refetchWorkspaces,
  } = useGetAllWorkspaces();

  const {
    data: users,
    isLoading: isUsersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useGetAllUsers();

  const {
    isLoading: isChannelsLoading,
    error: channelsError,
    refetch: refetchChannels,
  } = useGetAllChannels();

  const {
    data: workspaceConfig,
    isLoading: isWorkspaceConfigLoading,
    error: workspaceConfigError,
    refetch: refetchWorkspaceConfig,
  } = useGetWorkspace(activeWorkspaceId);

  // The role's permissions decide the sidebar and every route guard, so nothing
  // renders on the base role's preset and then snaps to the real role.
  const { isLoading: isAccessLoading } = useGetMe();

  // A workspace switch saves the pick and reloads, so the saved pick is almost
  // always still good: its own calls (channels, config, /users/me) start with it
  // alongside the list instead of after it. Without one, the list picks.
  useEffect(() => {
    if (activeWorkspaceId) return;

    const workspaceToActivate = workspaces
      ? getWorkspaceToActivate(workspaces)
      : getPreferredWorkspaceId();

    if (workspaceToActivate) {
      setActiveWorkspaceId(workspaceToActivate);
    }
  }, [activeWorkspaceId, workspaces, setActiveWorkspaceId]);

  // The saved pick is gone (deleted, or they were removed from it): drop it and
  // boot again on the first workspace. A reload, as a switch is, so nothing keeps
  // what the stale workspace answered.
  const isWorkspaceConfirmed = Boolean(
    workspaces?.some((workspace) => workspace.id === activeWorkspaceId),
  );
  const isWorkspaceStale = Boolean(
    activeWorkspaceId && workspaces?.length && !isWorkspaceConfirmed,
  );

  useEffect(() => {
    if (!isWorkspaceStale) return;

    clearPreferredWorkspaceId();
    window.location.reload();
  }, [isWorkspaceStale]);

  // Sync organization to store
  useEffect(() => {
    if (organization) {
      setOrganization(organization);
    }
  }, [organization, setOrganization]);

  // Sync workspace config to store
  useEffect(() => {
    if (workspaceConfig) {
      setActiveWorkspace(workspaceConfig);
    }
  }, [workspaceConfig, setActiveWorkspace]);

  // Sync logged-in user. In mock mode there is no Clerk session, so match the
  // seeded "current" mock user (falling back to the first) instead of an
  // externalId - otherwise loggedInUser stays null and any "me"-scoped UI (e.g.
  // My Activities) never loads.
  //
  // Bootstrap holds until this has run: the viewer's id is what every
  // "Mine"-scoped surface filters on, so resolving it after the first render
  // means those surfaces open unscoped and then snap.
  useEffect(() => {
    if (!users) return;

    if (isMockEnabled()) {
      const currentUserId = useMockStore.getState().currentUserId;

      updateLoggedInUser(
        users.find((user) => user.id === currentUserId) ?? users[0] ?? null,
      );
    } else if (userId && isSignedIn) {
      updateLoggedInUser(
        users.find((user) => user.externalId === userId) ?? null,
      );
    }

    setIsLoggedInUserResolved(true);
  }, [userId, isSignedIn, users, updateLoggedInUser]);

  // Only a 401 (the session itself is gone) signs out. A 403 is a missing
  // permission and lands on the error screen below with Retry and Sign out. A
  // saved pick's own calls fail for a workspace they've left; until the list
  // confirms it, that ends in the reload above, never a sign-out.
  const allErrors = [
    orgError,
    workspacesError,
    usersError,
    ...(isWorkspaceConfirmed ? [channelsError, workspaceConfigError] : []),
  ];

  const isUnauthorized = allErrors.some(isUnauthorizedError);

  const refetchBootstrap = useCallback(
    () =>
      Promise.all([
        refetchOrg(),
        refetchWorkspaces(),
        refetchUsers(),
        refetchChannels(),
        ...(activeWorkspaceId ? [refetchWorkspaceConfig()] : []),
      ]),
    [
      activeWorkspaceId,
      refetchOrg,
      refetchWorkspaces,
      refetchUsers,
      refetchChannels,
      refetchWorkspaceConfig,
    ],
  );

  // A 401 can be a cached token that lapsed, or the API briefly failing to
  // verify a good one: mint a fresh token and retry once before believing it.
  // Failing to reach Clerk for that token is an outage, not a sign-out.
  useEffect(() => {
    if (unauthorizedRecovery === "signingOut") return;

    if (!isUnauthorized) {
      if (
        unauthorizedRecovery === "refreshed" ||
        unauthorizedRecovery === "unavailable"
      ) {
        setUnauthorizedRecovery("idle");
      }

      return;
    }

    if (unauthorizedRecovery === "idle") {
      setUnauthorizedRecovery("refreshing");
      getToken({ skipCache: true }).then(
        async (token) => {
          if (token) await refetchBootstrap();
          setUnauthorizedRecovery("refreshed");
        },
        () => setUnauthorizedRecovery("unavailable"),
      );

      return;
    }

    if (unauthorizedRecovery !== "refreshed") return;

    // A saved pick from another organization is refused with a 401 as well:
    // drop it and boot again first, or signing back in would land here again.
    if (getPreferredWorkspaceId()) {
      clearPreferredWorkspaceId();
      window.location.reload();

      return;
    }

    setUnauthorizedRecovery("signingOut");
    reset();
    queryClient.clear();
    resetAnalytics();
    signOut();
  }, [
    isUnauthorized,
    unauthorizedRecovery,
    getToken,
    refetchBootstrap,
    reset,
    signOut,
  ]);

  if (
    unauthorizedRecovery === "signingOut" ||
    (isUnauthorized && unauthorizedRecovery === "refreshed")
  ) {
    return null;
  }

  if (isUnauthorized && unauthorizedRecovery !== "unavailable") {
    return <AuthLoadingScreen />;
  }

  // Signed in to a Clerk organization this backend has no record of.
  if (isNotFoundError(orgError)) {
    return <MissingOrganization />;
  }

  // Any other error, or a 401 whose fresh-token retry could not reach Clerk
  const hasError = allErrors.some((error) => error !== null);
  const hasNoWorkspaces = !isWorkspacesLoading && workspaces?.length === 0;

  // From "unavailable", going back to idle runs the fresh-token retry again.
  const handleRetry = () => {
    if (unauthorizedRecovery === "unavailable") {
      setUnauthorizedRecovery("idle");

      return;
    }

    refetchBootstrap();
  };

  const handleSignOut = () => {
    reset();
    queryClient.clear();
    resetAnalytics();
    signOut();
  };

  if (hasError || hasNoWorkspaces) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-5">
        <img alt={CONSTANTS.BRAND_NAME} height={50} src={logoFull} />
        <p className="text-muted">
          {hasNoWorkspaces
            ? "No workspace is available. Ask your organization administrator for access."
            : "Something went wrong while loading the app."}
        </p>
        <div className="flex items-center gap-3">
          <Button variant="primary" onPress={handleRetry}>
            Try Again
          </Button>
          <Button variant="ghost" onPress={handleSignOut}>
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  // Determine if bootstrap is complete
  const isBootstrapping =
    isOrgLoading ||
    isWorkspacesLoading ||
    isUsersLoading ||
    isChannelsLoading ||
    !isLoggedInUserResolved ||
    !isWorkspaceConfirmed ||
    isWorkspaceConfigLoading ||
    isAccessLoading ||
    workspaceConfig?.id !== activeWorkspaceId ||
    activeWorkspace?.id !== activeWorkspaceId;

  if (isBootstrapping) {
    return <AuthLoadingScreen />;
  }

  return <>{children}</>;
};
